import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Ticket, Clock, ArrowRight, Phone, Mail, MapPin, Sun, Moon, CreditCard, Bus } from "lucide-react";
import { motion } from "framer-motion";
import { publicApi } from "@/lib/publicApi";
import { useTheme } from "@/contexts/ThemeContext";

const LOGO_URL = "https://customer-assets-0z36b82j.emergentagent.net/job_ticketing-transport/artifacts/5p6cedk3_1000607872.png";

export default function PublicMenu() {
  const { companyId } = useParams();
  const [company, setCompany] = useState(null);
  const [error, setError] = useState(null);
  const { theme, toggle } = useTheme();

  useEffect(() => {
    publicApi.get(`/public/companies/${companyId}`)
      .then((r) => setCompany(r.data))
      .catch(() => setError("Cette agence n'existe pas ou n'est plus disponible."));
  }, [companyId]);

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 dark:bg-slate-950 p-6">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-2xl bg-red-100 dark:bg-red-500/20 grid place-items-center mx-auto text-red-500 mb-4"><Bus className="w-8 h-8" /></div>
          <h1 className="font-display text-2xl font-bold">Agence introuvable</h1>
          <p className="mt-2 text-slate-500">{error}</p>
          <Link to="/" className="mt-6 inline-block text-orange-500 font-semibold hover:underline">Retour à Transport.pro</Link>
        </div>
      </div>
    );
  }

  if (!company) return <div className="min-h-screen grid place-items-center text-slate-500">Chargement…</div>;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100">
      <header className="glass sticky top-0 z-30 h-16 flex items-center px-4 md:px-8">
        <Link to="/" className="flex items-center gap-2" data-testid="public-brand-link">
          <img src={LOGO_URL} alt="Transport.pro" className="w-9 h-9 rounded-lg object-cover" />
          <span className="font-display font-bold text-lg">Transport.pro</span>
        </Link>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={toggle} className="w-9 h-9 rounded-full grid place-items-center hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors" data-testid="public-theme-btn" aria-label="theme">
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-8 py-10 md:py-16 space-y-10">
        {/* Company hero */}
        <div className="flex flex-col md:flex-row gap-6 items-center md:items-start">
          {company.logo_url ? (
            <img src={company.logo_url} alt={company.name} className="w-24 h-24 md:w-28 md:h-28 rounded-2xl object-cover shadow-lg" data-testid="public-logo" />
          ) : (
            <div className="w-24 h-24 md:w-28 md:h-28 rounded-2xl bg-orange-500 text-white grid place-items-center shadow-lg"><Bus className="w-10 h-10" /></div>
          )}
          <div className="text-center md:text-left flex-1">
            <div className="text-xs font-semibold tracking-widest uppercase text-orange-500">Réservation en ligne</div>
            <h1 className="font-display text-3xl md:text-4xl font-bold mt-1" data-testid="public-company-name">{company.name}</h1>
            {company.description && <p className="mt-2 text-slate-500 max-w-2xl">{company.description}</p>}
            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 justify-center md:justify-start">
              {company.address && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {company.address}</span>}
              {company.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {company.phone}</span>}
              {company.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {company.email}</span>}
            </div>
          </div>
        </div>

        {/* Two big buttons */}
        <div className="grid md:grid-cols-2 gap-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
            <Link to={`/book/${companyId}/reservation`} data-testid="public-btn-reservation" className="group block">
              <div className="relative overflow-hidden rounded-3xl bg-orange-500 text-white p-8 min-h-[240px] flex flex-col justify-between hover:-translate-y-1 transition-transform shadow-lg shadow-orange-500/20">
                <div className="absolute -right-8 -top-8 w-48 h-48 rounded-full bg-white/10 blur-2xl"></div>
                <div className="absolute right-6 top-6"><Ticket className="w-9 h-9 opacity-70" /></div>
                <div>
                  <div className="text-xs uppercase tracking-widest font-semibold opacity-80">Billet</div>
                  <div className="font-display text-3xl md:text-4xl font-bold mt-2">Réservation</div>
                  <div className="mt-2 text-white/85 max-w-xs">Réservez à l'avance pour une date et un horaire précis.</div>
                </div>
                <div className="flex items-center gap-2 text-sm font-semibold">Commencer <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></div>
              </div>
            </Link>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Link to={`/book/${companyId}/realtime`} data-testid="public-btn-realtime" className="group block">
              <div className="relative overflow-hidden rounded-3xl bg-slate-900 text-white p-8 min-h-[240px] flex flex-col justify-between hover:-translate-y-1 transition-transform border border-slate-200/10 shadow-lg">
                <div className="absolute -left-8 -bottom-8 w-48 h-48 rounded-full bg-orange-500/25 blur-2xl"></div>
                <div className="absolute right-6 top-6"><Clock className="w-9 h-9 opacity-70" /></div>
                <div>
                  <div className="text-xs uppercase tracking-widest font-semibold opacity-80">Billet</div>
                  <div className="font-display text-3xl md:text-4xl font-bold mt-2">Temps réel</div>
                  <div className="mt-2 text-white/70 max-w-xs">Émettez un billet immédiat pour aujourd'hui.</div>
                </div>
                <div className="flex items-center gap-2 text-sm font-semibold">Commencer <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></div>
              </div>
            </Link>
          </motion.div>
        </div>

        <div className="p-4 rounded-2xl bg-orange-50 dark:bg-orange-500/10 border border-orange-200 dark:border-orange-500/30 text-sm text-slate-700 dark:text-slate-200 flex items-start gap-3">
          <CreditCard className="w-5 h-5 text-orange-500 mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold">Paiement en ligne uniquement</div>
            <div className="text-xs text-slate-500 mt-1">Wave, Fusion, Ciné Pay, Pay Tech… Pour un règlement en espèces, rendez-vous directement au guichet.</div>
          </div>
        </div>
      </main>

      <footer className="text-center text-xs text-slate-400 py-6">Propulsé par <Link to="/" className="text-orange-500 font-semibold">Transport.pro</Link></footer>
    </div>
  );
}
